<template>
  <el-header class="nav-bar" height="50px">
    <div class="nav-items">
      <!-- 主页按钮 -->
      <el-button
        type="primary"
        @click="handleGoToHome"
        class="home-btn"
      >
        <el-icon><House /></el-icon>
        主页
      </el-button>

      <!-- 返回实务按钮 -->
      <el-button
        type="primary"
        plain
        @click="handleBackToPractice"
        class="practice-btn"
      >
        <el-icon><Document /></el-icon>
        实务
      </el-button>

      <el-button
        type="primary"
        plain
        @click="handleComprehensiveTest"
      >
        <el-icon><Document /></el-icon>
        综合
      </el-button>
      
      <el-button plain @click="showDonateDialog = true">
        <el-icon><Coin /></el-icon>
        打赏
      </el-button>
      <el-button plain @click="handleContact">
        <el-icon><Document /></el-icon>
        必读
      </el-button>
      <template v-if="isAdmin">
        <el-dropdown @command="handleUserCommand">
          <el-button type="primary">
            管理员：{{ username }}
            <el-icon class="el-icon--right"><arrow-down /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="apiConfig">API配置</el-dropdown-item>
              <el-dropdown-item command="messageManagement">消息管理</el-dropdown-item>
              <el-dropdown-item command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </template>
      <template v-else>
        <!-- 非管理员用户可以访问 /admin/login 页面进行登录 -->
      </template>
    </div>
    <div class="search-area" v-if="!isComprehensiveTestPage">
      <el-input
        v-model="searchText"
        placeholder="输入后按回车键"
        @keyup.enter="handleSearch"
        clearable
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-button
        plain
        @click="handleTimer"
        :class="{ 'timer-active': isTimerRunning }"
      >
        <el-icon><Timer /></el-icon>
        {{ isTimerRunning ? timerDisplay : '计时' }}
      </el-button>
      <el-dropdown @command="handleAnalysisCommand" placement="bottom-end">
        <el-button type="primary" plain>
          <el-icon><DocumentChecked /></el-icon>
          分析
          <el-icon class="el-icon--right"><arrow-down /></el-icon>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="correction">
              <el-icon><Edit /></el-icon>
              批改
            </el-dropdown-item>
            <el-dropdown-item command="history">
              <el-icon><Clock /></el-icon>
              历史
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
    <div class="countdown">
      {{ countdownText }} {{ remainingDays }} {{ countdownSuffix }}
      <el-button
        @click="toggleFullscreen"
        class="fullscreen-btn"
      >
        <el-icon><FullScreen v-if="!isFullscreen" /><Aim v-else /></el-icon>
        {{ isFullscreen ? 'ESC' : '全屏' }}
      </el-button>
    </div>

    <!-- 搜索结果对话框 -->
    <SearchResults
      v-model:visible="showSearchResults"
      :results="searchResults"
    />

    <!-- 添加用户信息对话框 -->
    <el-dialog
      v-model="showUserInfoDialog"
      title="用户信息"
      width="50%"
      :close-on-click-modal="false"
      :append-to-body="true"
      :modal-append-to-body="true"
      class="user-info-dialog"
    >
      <div class="user-info-details">
        <div v-if="userInfo">
          <div class="detail-item">
            <h4>基本信息</h4>
            <p>用户名：{{ userInfo.profile?.username || '未设置' }}</p>
            <p>邮箱：{{ userInfo.email }}</p>
            <p>注册时间：{{ formatDate(userInfo.created_at) }}</p>
          </div>
        </div>
        <div v-else>
          加载中...
        </div>
      </div>
    </el-dialog>

    <!-- 打赏二维码对话框 -->
    <el-dialog
      v-model="showDonateDialog"
      title="支持我们"
      width="340px"
      :close-on-click-modal="true"
      class="donate-dialog"
    >
      <div class="donate-content">
        <img src="/images/donate-qr.png" alt="打赏二维码" class="donate-qr" />
        <div class="donate-desc">
          您的每一份支持都将转化为：<br />
          ❤ 服务器续费<br />
          🛠 持续开发维护<br />
          ☕ 深夜码农的咖啡燃料<br />
          🐈 小猫最爱的水煮鸡胸肉
        </div>
      </div>
    </el-dialog>
  </el-header>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElLoading } from 'element-plus'
import { Search, ArrowDown, House, Coin, Document, Timer, Edit, Clock, FullScreen, Aim, DocumentChecked } from '@element-plus/icons-vue'
import SearchResults from './SearchResults.vue'
import dayjs from 'dayjs'
import { AITranslationService } from '@/service/aiTranslation'
import request from '@/utils/request'
import { useRouter, useRoute } from 'vue-router'
import LocalStorageService from '@/config/localStorage'

export default {
  name: 'NavBar',
  components: {
    Search,
    ArrowDown,
    House,
    Coin,
    Document,
    Timer,
    Edit,
    Clock,
    FullScreen,
    Aim,
    DocumentChecked,
    SearchResults
  },
  props: {
    showLogin: {
      type: Boolean,
      required: true
    },
    showContact: {
      type: Boolean,
      required: true
    },
    showApiConfig: {
      type: Boolean,
      required: true
    }
  },
  emits: [
    'update:showLogin',
    'update:showContact',
    'update:showApiConfig'
  ],
  setup(props, { emit }) {
    const store = useStore()
    const router = useRouter()
    const route = useRoute()
    const searchText = ref('')
    const showSearchResults = ref(false)
    const searchResults = ref([])
    const showUserInfoDialog = ref(false)
    const loading = ref(false)

    const isTimerRunning = ref(false)
    const timerDisplay = ref('00:00')
    const isFullscreen = ref(false)
    const wakeLock = ref(null)
    const showDonateDialog = ref(false)

    // 倒计时配置（从后端读取）
    const countdownConfig = ref({
      text: '距离CATTI还剩',
      suffix: '天',
      targetDate: '2026-05-23'
    })

    // 从后端获取倒计时配置
    const fetchCountdownConfig = async () => {
      try {
        const response = await request.get('/api/countdown')
        if (response.data) {
          countdownConfig.value = {
            text: response.data.text || '距离CATTI还剩',
            suffix: response.data.suffix || '天',
            targetDate: response.data.targetDate || '2026-05-23'
          }
        }
      } catch (error) {
        console.error('获取倒计时配置失败:', error)
        // 使用默认配置
      }
    }

    // 计算剩余天数
    const remainingDays = computed(() => {
      const today = new Date()
      const target = new Date(countdownConfig.value.targetDate)
      const diffTime = Math.abs(target - today)
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    })

    // 获取倒计时文本
    const countdownText = computed(() => {
      return countdownConfig.value.text
    })

    // 获取倒计时单位后缀
    const countdownSuffix = computed(() => {
      return countdownConfig.value.suffix
    })

    // 用户状态
    const isAdmin = computed(() => store.state.isAdmin || false)
    // 移除登录状态检查，所有用户都可以使用功能
    const username = computed(() => store.state.adminUsername)

    // 判断是否是综合考试页面
    const isComprehensiveTestPage = computed(() => route.path === '/comprehensive-test')


    // 计时器相关方法
    const handleTimer = () => {
      if (!isTimerRunning.value) {
        isTimerRunning.value = true
        startTimer()
      } else {
        isTimerRunning.value = false
        stopTimer()
      }
    }

    let timerInterval = null
    const startTimer = () => {
      let seconds = 0
      timerInterval = setInterval(() => {
        seconds++
        const minutes = Math.floor(seconds / 60)
        const remainingSeconds = seconds % 60
        timerDisplay.value = `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
      }, 1000)
    }

    const stopTimer = () => {
      if (timerInterval) {
        clearInterval(timerInterval)
        timerInterval = null
        timerDisplay.value = '00:00'
      }
    }

    const handleSearch = async () => {
      if (searchText.value) {
        const loading = ElLoading.service({
          text: '搜索中...',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        
        try {
          // 保存搜索关键词到store
          store.commit('setSearchQuery', searchText.value)
          
          console.log('发起搜索:', searchText.value)
          const response = await request.post('/api/search', { query: searchText.value })
          searchResults.value = response.results || []
          
          if (searchResults.value.length === 0) {
            ElMessage.info('未找到匹配的内容')
          } else {
            ElMessage.success(`找到 ${searchResults.value.length} 条匹配结果`)
            showSearchResults.value = true
          }
        } catch (error) {
          console.error('搜索处理错误:', error)
          ElMessage.error(error.message || '搜索失败，请稍后重试')
        } finally {
          loading.close()
        }
      } else {
        ElMessage.warning('请输入搜索关键词')
      }
    }

    const handleContact = () => {
      emit('update:showContact', true)
    }

    const handleAnalysisCommand = (command) => {
      try {
        switch (command) {
          case 'correction':
            handleCorrection()
            break
          case 'history':
            handleHistory()
            break
          default:
            console.warn('未知分析命令:', command)
        }
      } catch (error) {
        console.error('处理分析命令失败:', error)
        ElMessage.error('操作失败，请重试')
      }
    }

    const handleUserCommand = async (command) => {
      try {
        switch (command) {
          case 'logout':
            await handleLogout()
            break
          case 'apiConfig':
            router.push('/admin/api-config')
            break
          case 'messageManagement':
            router.push('/admin/message')
            break
          default:
            console.warn('未知命令:', command)
        }
      } catch (error) {
        console.error('处理用户命令失败:', error)
        ElMessage.error('操作失败，请重试')
      }
    }

    const handleLogout = async () => {
      console.log('开始退出登录')
      try {
        if (isAdmin.value) {
          await store.dispatch('adminLogout')
        } else {
          await store.dispatch('logout')
        }
        
        // 更新状态
        store.commit('setUserStatus', null)
        
        ElMessage.success('已退出登录')
        
        // 显示登录对话框
        emit('update:showLogin', true)
      } catch (error) {
        console.error('退出登录失败:', error)
        ElMessage.error('退出登录失败：' + error.message)
      }
    }

    const formatDate = (date) => {
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
    }

    // 处理全屏切换
    const toggleFullscreen = async () => {
      if (!document.fullscreenElement) {
        // 进入全屏
        document.documentElement.requestFullscreen({
          navigationUI: 'hide'
        }).then(() => {
          isFullscreen.value = true
          
          // 请求屏幕唤醒锁
          if ('wakeLock' in navigator) {
            try {
              navigator.wakeLock.request('screen').then(lock => {
                wakeLock.value = lock
                console.log('屏幕唤醒锁已激活')
              }).catch(err => {
                console.error('无法获取屏幕唤醒锁:', err)
              })
              
              // 监听可见性变化，在页面重新可见时重新请求唤醒锁
              document.addEventListener('visibilitychange', handleVisibilityChange)
            } catch (err) {
              console.error('无法获取屏幕唤醒锁:', err)
            }
          } else {
            console.warn('您的浏览器不支持屏幕唤醒锁API')
          }
        }).catch(err => {
          console.error('进入全屏失败:', err)
        })
      } else {
        // 退出全屏
        if (document.exitFullscreen) {
          document.exitFullscreen()
          isFullscreen.value = false
          
          // 释放屏幕唤醒锁
          if (wakeLock.value !== null) {
            wakeLock.value.release()
              .then(() => {
                console.log('屏幕唤醒锁已释放')
                wakeLock.value = null
              })
              .catch((err) => {
                console.error('释放屏幕唤醒锁失败:', err)
              })
            
            // 移除可见性变化监听
            document.removeEventListener('visibilitychange', handleVisibilityChange)
          }
        }
      }
    }

    // 添加处理可见性变化的函数
    const handleVisibilityChange = async () => {
      if (wakeLock.value === null && 
          document.visibilityState === 'visible' && 
          document.fullscreenElement) {
        try {
          wakeLock.value = await navigator.wakeLock.request('screen')
          console.log('屏幕唤醒锁已重新激活')
        } catch (err) {
          console.error('重新获取屏幕唤醒锁失败:', err)
        }
      }
    }

    // 监听全屏变化
    if (document.addEventListener) {
      document.addEventListener('fullscreenchange', () => {
        isFullscreen.value = !!document.fullscreenElement
        
        // 如果意外退出全屏（如按ESC键），也需要释放唤醒锁
        if (!document.fullscreenElement && wakeLock.value) {
          wakeLock.value.release()
            .then(() => {
              console.log('退出全屏：屏幕唤醒锁已释放')
              wakeLock.value = null
            })
            .catch((err) => {
              console.error('退出全屏：释放屏幕唤醒锁失败:', err)
            })
          
          document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
      })
    }

    // 处理解析按钮点击
    const handleAnalysis = async () => {
      try {
        // 获取ContentArea组件实例
        const contentAreaInstance = store.state.contentAreaInstance;
        const currentFile = store.state.currentFile;
        
        if (!currentFile) {
          ElMessage.warning('请先选择文件');
          return;
        }
        
        // 检查当前是否选择了段落
        let currentPassageIndex = null;
        if (contentAreaInstance && contentAreaInstance.getCurrentPassages) {
          const passageInfo = contentAreaInstance.getCurrentPassages();
          currentPassageIndex = passageInfo.currentIndex;
        }
        
        // 创建彩色loading
        let loading = createColorfulLoading(currentPassageIndex !== null ? 
                                         `正在解析 P${currentPassageIndex + 1}...` : 
                                         '正在解析...');
        
        try {
          // 创建正确的文件路径，根据当前是否选中段落
          let filePath;
          if (currentPassageIndex !== null) {
            // 使用p1或p2标识段落特定的文件
            filePath = currentFile.path.replace(/\.json$/, `.p${currentPassageIndex + 1}.analysis.txt`);
            console.log('获取段落解析文件:', filePath);
          } else {
            // 使用常规的文件路径
            filePath = currentFile.path.replace(/\.json$/, '.analysis.txt');
            console.log('获取整篇文章解析文件:', filePath);
          }
          
          // 加载已有的解析结果
          const savedResult = await request.get(`/api/get-translation?path=${filePath}`)
          if (savedResult && savedResult.content) {
            closeColorfulLoading(loading);
            store.commit('setAiDialogVisible', true);
            store.commit('setAIDialogTitle', currentPassageIndex !== null ? `P${currentPassageIndex + 1} 解析` : '解析');
            store.commit('setAIDialogContent', savedResult.content);
          } else {
            // 如果没有找到已保存的结果，调用API生成
            AITranslationService.getAnalysis(store)
              .finally(() => {
                closeColorfulLoading(loading);
              });
          }
        } catch (error) {
          console.error('加载解析结果失败:', error);
          // 如果加载失败，调用API生成新的结果
          AITranslationService.getAnalysis(store)
            .finally(() => {
              closeColorfulLoading(loading);
            });
        }
      } catch (error) {
        console.error('解析处理失败:', error);
        ElMessage.error('解析处理失败: ' + error.message);
      }
    };

    // 检查批改次数限制
    const checkCorrectionLimit = async () => {
      // 管理员无限制
      if (isAdmin.value) {
        return true
      }
      
      try {
        // 使用本地存储检查批改次数限制
        const result = await LocalStorageService.checkCorrectionLimit()
        return result.canUse
      } catch (error) {
        console.error('检查批改次数限制失败:', error)
        // 如果检查失败，默认允许使用
        return true
      }
    }
    
    // 处理批改按钮点击
    const handleCorrection = async () => {
      // 移除登录检查，所有用户都可以使用批改功能

      // 检查批改次数限制
      const canProceed = await checkCorrectionLimit()
      if (!canProceed) {
        ElMessage.warning('今日批改次数已用完，请明天再试')
        return
      }

      // 使用转场动画提示用户正在处理
      let loading = createColorfulLoading('正在批改...')
      
      try {
        // 调用批改API
        await AITranslationService.getScore(store)
        
        // 只有在成功调用大语言模型，获取评分结果后才增加使用次数
        await LocalStorageService.incrementCorrectionCount()
        ElMessage.success('批改完成')
      } catch (error) {
        console.error('批改失败:', error)
        ElMessage.error('批改失败，请稍后重试')
      } finally {
        closeColorfulLoading(loading)
      }
    }

    const handleHistory = () => {
      try {
        // 使用全局事件总线触发openScoreHistory事件
        const event = new CustomEvent('openScoreHistory');
        window.dispatchEvent(event);
      } catch (error) {
        console.error('打开历史记录失败:', error);
      }
    }

    // 在onBeforeUnmount中添加释放锁的代码
    onBeforeUnmount(() => {
      // 释放屏幕唤醒锁
      if (wakeLock.value !== null) {
        wakeLock.value.release()
          .then(() => {
            console.log('组件卸载：屏幕唤醒锁已释放')
            wakeLock.value = null
          })
          .catch((err) => {
            console.error('组件卸载：释放屏幕唤醒锁失败:', err)
          })
        
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    })

    // 添加onMounted钩子，在组件挂载时从服务器获取最新配置
    onMounted(async () => {
      // 从服务器获取最新的倒计时配置
      await fetchCountdownConfig()
    })

    // 修改createColorfulLoading函数，使用ElLoading
    const createColorfulLoading = (text) => {
      // 创建自定义loading实例
      const loading = ElLoading.service({
        text: text,
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'colorful-loading-custom'
      })
      
      // 使用setTimeout确保DOM已完全加载
      setTimeout(() => {
        // 尝试多种选择器定位加载文本元素
        const loadingText = document.querySelector('.colorful-loading-custom .el-loading-text') || 
                            document.querySelector('.el-loading-text');
        if (!loadingText) {
          console.error('未找到loading文本元素');
          return;
        }
        
        // 隐藏默认的旋转图标
        const spinner = document.querySelector('.colorful-loading-custom .circular') || 
                        document.querySelector('.el-loading-spinner .circular');
        if (spinner) {
          spinner.style.display = 'none';
        }
        
        // 使用内联样式并设置!important确保优先级最高
        loadingText.setAttribute('style', `
          font-size: 28px !important;
          text-align: center !important;
          width: 100% !important;
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          margin: 0 !important;
          padding: 0 !important;
          white-space: pre-line !important;
          font-weight: bold !important;
          line-height: 1.6 !important;
          z-index: 9999 !important;
        `);
        
        // 添加一个内联样式标签以确保我们的样式覆盖全局样式
        const styleElement = document.createElement('style');
        styleElement.innerHTML = `
          .colorful-loading-custom .el-loading-text {
            font-size: 28px !important;
            text-align: center !important;
            width: 100% !important;
            position: absolute !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            margin: 0 !important;
            padding: 0 !important;
            white-space: pre-line !important;
            font-weight: bold !important;
            z-index: 9999 !important;
          }
          .colorful-loading-custom .el-loading-spinner .circular {
            display: none !important;
          }
        `;
        document.head.appendChild(styleElement);
        
        // 保存样式元素引用以便后续清除
        loading.styleElement = styleElement;
        
        // 定义颜色数组
        const colors = [
          '#67C23A', // 绿色
          '#409EFF', // 蓝色
          '#E6A23C', // 黄色
          '#F56C6C'  // 红色
        ];
        
        let colorIndex = 0;
        
        // 创建颜色动画循环，修复颜色设置方法
        const colorAnimation = setInterval(() => {
          if (loadingText) {
            // 使用setAttribute方法正确设置颜色样式
            loadingText.setAttribute('style', `
              font-size: 28px !important;
              text-align: center !important;
              width: 100% !important;
              position: absolute !important;
              top: 50% !important;
              left: 50% !important;
              transform: translate(-50%, -50%) !important;
              margin: 0 !important;
              padding: 0 !important;
              white-space: pre-line !important;
              font-weight: bold !important;
              line-height: 1.6 !important;
              z-index: 9999 !important;
              color: ${colors[colorIndex]} !important;
            `);
            colorIndex = (colorIndex + 1) % colors.length;
          }
        }, 400); // 加快颜色变化速度
        
        // 保存动画ID到loading实例以便后续清除
        loading.colorAnimation = colorAnimation;
      }, 100);
      
      return loading;
    };

    // 修改closeColorfulLoading函数，清除自定义样式元素
    const closeColorfulLoading = (loading) => {
      if (!loading) return;
      
      // 清除颜色动画
      if (loading.colorAnimation) {
        clearInterval(loading.colorAnimation);
      }
      
      // 清除样式元素
      if (loading.styleElement) {
        document.head.removeChild(loading.styleElement);
      }
      
      // 关闭loading
      loading.close();
    };

    const handleGoToHome = () => {
      router.push('/')
    }

    const handleBackToPractice = () => {
      router.push('/practice')
    }

    const handleComprehensiveTest = () => {
      router.push('/comprehensive-test')
    }

    
    return {
      searchText,
      showSearchResults,
      searchResults,
      showUserInfoDialog,
      handleSearch,
      handleContact,
      handleAnalysisCommand,
      formatDate,
      handleUserCommand,
      handleLogout,
      handleAnalysis,
      handleCorrection,
      handleHistory,
      handleTimer,
      isTimerRunning,
      timerDisplay,
      toggleFullscreen,
      isFullscreen,
      remainingDays,
      countdownText,
      countdownSuffix,
      username,
      isAdmin,
      loading,
      handleGoToHome,
      handleBackToPractice,
      handleComprehensiveTest,
      showDonateDialog,
      isComprehensiveTestPage
    }
  }
}
</script>

<style scoped>
.nav-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 15px 0 15px;
  margin-right: 16px;
  background-color: #fff;
  border-bottom: 1px solid #e8e8e8;
  height: 50px !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  font-size: 13px;
}

.nav-items {
  display: flex;
  gap: 5px;
  flex-shrink: 0;
}

.nav-items .el-button {
  height: 30px;
  padding: 0 10px;
  font-size: 13px;
}

.search-area {
  display: flex;
  align-items: center;
  margin: 0 8px;
  gap: 5px;
  flex-shrink: 0;
}

.search-area .el-input {
  width: 140px;
  font-size: 13px;
}

.search-area :deep(.el-input__inner) {
  font-size: 13px;
  height: 30px;
  padding: 0 8px;
}

.search-area .el-button {
  height: 30px;
  padding: 0 10px;
  font-size: 13px;
}

.countdown {
  white-space: nowrap;
  color: #606266;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 0;
  flex-shrink: 0;
  min-width: 150px;
}

.timer-active {
  color: #f56c6c !important;
  border-color: #f56c6c !important;
}

.timer-active:hover {
  color: #f56c6c !important;
  border-color: #f56c6c !important;
  background-color: #fef0f0 !important;
}

.user-info-details {
  padding: 20px;
}

.detail-item {
  margin-bottom: 20px;
}

/* 图标颜色样式 */
.nav-items .el-button .el-icon {
  margin-right: 4px;
}

/* 主页按钮样式 */
.home-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important;
  font-weight: 600 !important;
  color: #fff !important;
  transition: all 0.3s ease !important;
  animation: homeBtnPulse 2s ease-in-out infinite;
}

.home-btn:hover {
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6) !important;
  transform: translateY(-2px) !important;
}

.home-btn .el-icon {
  color: #fff !important;
  filter: drop-shadow(0 0 2px rgba(255, 255, 255, 0.5));
}

@keyframes homeBtnPulse {
  0%, 100% {
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  50% {
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6), 0 0 20px rgba(102, 126, 234, 0.3);
  }
}

/* 打赏图标 - 现在是第4个按钮（主页、实务、综合、打赏）*/
.nav-items .el-button:nth-child(4) .el-icon {
  color: #f56c6c;
}

/* 必读图标 - 现在是第5个按钮 */
.nav-items .el-button:nth-child(5) .el-icon {
  color: #e6a23c;
}

/* 搜索区域按钮图标颜色 */
.search-area .el-button .el-icon {
  margin-right: 4px;
}

/* 计时图标 */
.search-area .el-button:nth-child(2) .el-icon {
  color: #ff9800;
}

/* 分析下拉菜单图标 */
.search-area .el-dropdown:nth-child(3) .el-button .el-icon {
  color: #67c23a;
}

/* 分析下拉菜单选项图标颜色 */
.search-area .el-dropdown .el-dropdown-menu .el-dropdown-item .el-icon:first-child {
  margin-right: 8px;
}

/* 批改选项图标 */
.search-area .el-dropdown .el-dropdown-menu .el-dropdown-item:first-child .el-icon:first-child {
  color: #f56c6c;
}

/* 历史选项图标 */
.search-area .el-dropdown .el-dropdown-menu .el-dropdown-item:last-child .el-icon:first-child {
  color: #607d8b;
}

/* 全屏按钮图标 */
.countdown .el-button .el-icon {
  color: #409eff;
}

.detail-item h4 {
  margin: 0 0 10px;
  color: #303133;
  font-size: 16px;
}

.detail-item p {
  margin: 8px 0;
  color: #606266;
}

.user-info-dialog {
  z-index: 3000 !important;
}

:deep(.el-dialog__wrapper) {
  z-index: 2999 !important;
}

:deep(.el-overlay) {
  z-index: 2998 !important;
}



.fullscreen-btn {
  padding: 6px 10px !important;
  height: 30px !important;
  font-size: 13px !important;
}

/* 隐藏浏览器全屏提示 */
::backdrop {
  display: none !important;
}

/* 覆盖全局CSS */
:deep(.colorful-loading-custom .el-loading-text) {
  white-space: pre-line !important;
  text-align: left !important;
  margin-top: 15px !important;
  line-height: 2 !important;
  width: 250px !important;
  display: block !important;
  word-break: break-all !important;
}

/* 更多的CSS选择器覆盖ElLoading组件 */
:global(.colorful-loading-custom.el-loading-mask .el-loading-text) {
  white-space: pre-line !important;
  width: 250px !important;
  max-width: 80% !important;
  display: block !important;
}

/* 使用全局样式确保加载文本可以换行 */
:global(.colorful-loading-custom.el-loading-mask .el-loading-spinner .el-loading-text) {
  white-space: pre-line !important;
  text-align: left !important;
  line-height: 2 !important;
  width: 250px !important;
}

:deep(.colorful-loading-custom .el-loading-spinner) {
  width: 100%;
  text-align: center;
}

:deep(.colorful-loading-custom .el-loading-text) {
  margin: 0;
  font-weight: bold;
  white-space: pre-line;
}

.custom-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 22px;
  padding-left: 8px;
  box-sizing: border-box;
}

:deep(.title-member) {
  color: #9365FB !important;
  font-weight: bold;
  font-size: 18px;
  background: linear-gradient(to right, #9365FB, #B995FD);
  color: transparent;
  background-clip: text;
}

.nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.donate-dialog .el-dialog__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 10px 10px 10px;
}
.donate-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.donate-qr {
  width: 180px;
  height: 180px;
  margin-bottom: 18px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  border: 1px solid #eee;
}
.donate-desc {
  font-size: 15px;
  color: #606266;
  text-align: center;
  line-height: 2;
  font-weight: 500;
  letter-spacing: 0.5px;
}
</style>

<style>
/* 全局样式覆盖Element Plus的Loading组件 */
.colorful-loading-custom.el-loading-mask .el-loading-text {
  white-space: pre-line !important;
  width: 100% !important;
  text-align: center !important;
  line-height: 1.8 !important;
  font-size: 14px !important;
  display: block !important;
  margin: 0 !important;
  padding: 0 !important;
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  max-width: 80% !important;
  color: #409EFF !important;
  z-index: 2010 !important;
}

.colorful-loading-custom.el-loading-mask .el-loading-spinner {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 2009 !important;
}

/* 隐藏加载圈圈 */
.colorful-loading-custom.el-loading-mask .el-loading-spinner .circular {
  display: none !important;
}

/* 确保蒙层正确覆盖整个页面 */
.colorful-loading-custom.el-loading-mask {
  position: fixed !important;
  z-index: 2000 !important;
}

/* 增加全局样式，确保样式能被应用 */
.colorful-loading-custom.el-loading-mask .el-loading-text {
  font-size: 28px !important;
  text-align: center !important;
  width: 100% !important;
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  padding: 0 !important;
  white-space: pre-line !important;
  font-weight: bold !important;
}

.colorful-loading-custom.el-loading-mask .el-loading-spinner .circular {
  display: none !important;
}
</style>
